import * as Yup from 'yup';
import { calculationType } from './tabs/DocFees/DocFeesTable/interfaces';

const maxError = (value = 128) => `Max ${value} characters only`;
const minError = (value: number) => `The min value must be ${value} or greater`;
const integerError = 'Enter an integer';
const maxFicoError = 'Max Score value should be greater than Min Score';

export const formulaValidationSchema = Yup.object().shape({
  formulaType: Yup.string().nullable().required(),
  formulaValue: Yup.string().nullable().required(),
});

export const docFeesValidationSchema = Yup.object().shape({
  state: Yup.string().nullable().required(),
  calculationType: Yup.string().nullable().required(),
  amount: Yup.string().nullable().when('calculationType', {
    is: (type: number) => {
      const validateAmount = type !== calculationType.Formula && !Yup.ref('amount').isValue;
      const validateFormula = type !== calculationType.FlatFee && !Yup.ref('formula').isValue;
      return validateAmount || validateFormula;
    },
    then: Yup.string().nullable().required(),
    otherwise: Yup.string().nullable(),
  }),
  category: Yup.string().required().min(1, minError(1)).max(128, maxError()),
  description: Yup.string().required().min(1, minError(1)).max(128, maxError()),
});

export const lenderUtilityCardsValidationSchema = Yup.object().shape({
  fromDate: Yup.date().typeError('From Date is invalid').required().nullable(),
  toDate: Yup.date().typeError('To Date is invalid').nullable().min(Yup.ref('fromDate'), 'The end date must be after the start date'),
});

export const lenderUtilityTiersValidationSchema = Yup.object().shape({
  tierLabel: Yup.string().required().max(128, maxError()),
  tierDescription: Yup.string().min(0, minError(0)).max(128, maxError()).nullable(),
  minFico: Yup.number()
    .min(0, minError(0))
    .max(32767, 'Min Score must be less than or equal to 32767')
    .integer(integerError)
    .nullable()
    .when('maxFico', (maxFico, schema) => {
      return schema.test({
        name: 'minFico',
        exclusive: false,
        message: 'The min value must be less than the max value',
        test: function(value: number) {
          return !value || !maxFico || value < maxFico;
        },
      });
    }),
  maxFico: Yup.number()
    .min(0, minError(0))
    .max(32767, 'Max Score must be less than or equal to 32767')
    .integer(integerError)
    .nullable(),
});

export const lenderUtilityConditionsValidationSchema = Yup.object().shape({
  rate: Yup.number().required().min(0, minError(0)),
  termGroup: Yup.number().required().min(0, minError(0)).integer(integerError),
  ficoGroup: Yup.number().required().min(0, minError(0)).integer(integerError),
  maxFlatFeePercent: Yup.number().min(0, minError(0)),
  type: Yup.string().nullable(),
  minTotalAmountFinanceGreen: Yup.number().min(0, minError(0)),
  minTotalAmountFinanceYellow: Yup.number().min(0, minError(0)),
  maxAgeGreen: Yup.number().min(0, minError(0)).integer(integerError),
  minAgeGreen: Yup.number().min(0, minError(0)).integer(integerError),
  maxAgeYellow: Yup.number().min(0, minError(0)).integer(integerError),
  maxMilesGreen: Yup.number().min(0, minError(0)).integer(integerError),
  maxMilesYellow: Yup.number().min(0, minError(0)),
  maxLtvGreen: Yup.number().min(0, minError(0)),
  maxLtvYellow: Yup.number().min(0, minError(0)),
  maxFico: Yup.number().min(0, minError(0)).integer(integerError),
  maxMarkup: Yup.number().min(0, minError(0)),
  maxAllInLtv: Yup.number().min(0, minError(0)),
  maxLoanAmount: Yup.number().min(0, minError(0)),
  maxDti: Yup.number().min(0, minError(0)),
  maxPti: Yup.number().min(0, minError(0)),
  maxFlatGreen: Yup.number().min(0, minError(0)),
});

export const additionalDocFeesValidationSchema = Yup.object().shape(
  {
    category: Yup.string().required().min(1, minError(1)).max(128, maxError()),
    description: Yup.string().nullable().min(1, minError(1)).max(128, maxError()),
    amount: Yup.number().required().min(0, minError(0)),
    isCurrent: Yup.boolean(),
    isIncluded: Yup.boolean(),
    isDocFee: Yup.boolean(),
    minFico: Yup.number()
      .min(0, minError(0))
      .nullable()
      .when('maxFico', {
        is: (maxFico: number) => maxFico !== undefined && maxFico !== null,
        then: Yup.number().required(),
        otherwise: Yup.number().nullable(),
      }),
    maxFico: Yup.number()
      .min(0, minError(0))
      .when('minFico', {
        is: (minFico: number) => minFico !== undefined && minFico !== null,
        then: Yup.number().required().min(Yup.ref('minFico'), maxFicoError),
        otherwise: Yup.number().nullable(),
      }),
  },
  [['minFico', 'maxFico']],
);

export const creditRulesValidationSchema = Yup.object().shape({
  creditRuleType: Yup.string().required(),
  condition: Yup.string().required(),
  value: Yup.string().required().nullable(),
});

export const formulaFormValidationSchema = Yup.object().shape({
  formula: Yup.string().nullable().required(),
});
